// Conversion events sent once a checkout succeeds. They run once per Stripe
// payment (a Stripe return page can confirm the same payment more than once),
// each call is isolated so one failure doesn't drop the rest, and none of them
// can throw into the checkout.
import {
  isConfirmedTrialStart,
  PaymentTracking,
  safely,
  trackOnce,
  triggerBingTrialStartEvent,
  triggerTrialStartedEvent,
} from "~/helpers/conversionTracking";
import {
  triggerCheckoutPageView,
  triggerGtagPurchaseEvent,
  triggerStartedTrialEvent,
} from "~/helpers/gtag";
import {
  triggerGTMAddPaymentInfo,
  triggerGTMDowngrade,
  triggerGTMPlanSubscription,
  triggerGTMStartTrial,
  triggerGTMUpgrade,
} from "~/helpers/gtm";
import { getCheckoutAction } from "~/helpers/plans";
import { statsigClient } from "~/helpers/statsig";
import { IAccount } from "~/types/account";
import { IPlan } from "~/types/billing";

// The account the page holds can already show the new subscription by the
// time Stripe sends the customer back, which reads as an upgrade. A trial the
// API confirmed is a trial whatever that account says.
const checkoutActionFor = (
  account: IAccount | null,
  plan: IPlan,
  tracking: PaymentTracking | undefined
) => {
  const trialStarted = isConfirmedTrialStart(tracking, plan);
  return {
    trialStarted,
    action: trialStarted ? "trial" : getCheckoutAction(account, plan),
  };
};

// Checkout page. Shopify calls this when the charge is created, with no
// tracking: only the events it always sent fire then.
export const trackCheckoutSuccess = (
  account: IAccount | null,
  plan: IPlan,
  tracking?: PaymentTracking
) =>
  trackOnce(tracking?.intentId, () => {
    const { trialStarted, action } = checkoutActionFor(account, plan, tracking);

    safely(() => statsigClient.logEvent("checkout_trial_started"));
    safely(() => triggerGtagPurchaseEvent(plan));
    safely(() => triggerGTMPlanSubscription(plan));
    safely(() => triggerCheckoutPageView());
    if (trialStarted) safely(() => triggerTrialStartedEvent(tracking, plan));
    if (trialStarted) safely(() => triggerBingTrialStartEvent(tracking, plan));

    safely(() => {
      switch (action) {
        case "trial":
          return triggerStartedTrialEvent();
        case "downgrade":
          return triggerGTMDowngrade(plan);
        case "upgrade":
          return triggerGTMUpgrade(plan);
        case "firstSubscription":
          return triggerGTMAddPaymentInfo();
      }
    });
  });

// Annual plans modal.
export const trackAnnualPlanSuccess = (
  account: IAccount | null,
  plan: IPlan | undefined,
  tracking?: PaymentTracking
) =>
  trackOnce(tracking?.intentId, () => {
    if (!plan) return;
    const { trialStarted, action } = checkoutActionFor(account, plan, tracking);

    safely(() => triggerGTMPlanSubscription(plan));
    if (trialStarted) safely(() => triggerTrialStartedEvent(tracking, plan));
    if (trialStarted) safely(() => triggerBingTrialStartEvent(tracking, plan));

    safely(() => {
      switch (action) {
        case "trial":
          return triggerGTMStartTrial(plan);
        case "downgrade":
          return triggerGTMDowngrade(plan);
        case "upgrade":
          return triggerGTMUpgrade(plan);
        case "firstSubscription":
          return triggerGTMAddPaymentInfo();
      }
    });
  });
